import { Component, Input, ViewChildren, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CategoryBase, Language, CategoryTranslationCollectionDto, CategoryTranslationDto } from '../../../shared/model';
import { SharedService } from '../../../shared/shared.service';

@Component({
  selector: 'app-edit-category',
  templateUrl: './edit-category.component.html',
  styleUrls: ['../../../shared/styles/edit-modal.css', '../keywords-view.component.css']
})
export class EditCategoryComponent implements AfterViewInit {

  @Input() category: CategoryBase;
  @Input() categoryTranslations: CategoryTranslationDto[];
  @Input() languages: Language[];

  @ViewChildren('description') descriptions;

  myForm: FormGroup;
  nameMissing = false;
  descriptionMissing = false;
  flags: any;
  placeholderTranslation: string = null;

  constructor(
    public activeModal: NgbActiveModal,
    private sharedService: SharedService,
    private formBuilder: FormBuilder) {
      this.createForm();
      this.flags = this.sharedService.getFlags();
  }

  ngAfterViewInit() {
    this.descriptions._results.forEach(d => {
      this.adjustElementHeight(d.nativeElement);
    });

    this.setFormValues(this.category);
  }

  createForm() {
    this.myForm = this.formBuilder.group({
      Id: '',
      EnglishName: '',
      EnglishDescription: '',
      FrenchName: '',
      FrenchDescription: '',
      GermanName: '',
      GermanDescription: '',
      SpanishName: '',
      SpanishDescription: '',
      DutchName: '',
      DutchDescription: '',
      ItalianName: '',
      ItalianDescription: ''
    });
  }

  setFormValues(category: CategoryBase) {
    this.myForm.controls['Id'].setValue(category.Id);
    this.myForm.controls['EnglishName'].setValue(category.Name);
    this.myForm.controls['EnglishDescription'].setValue(category.Description);
    if (!!this.categoryTranslations) {
      this.getValuesFromTranslationsToForm();
      this.setEnglishTranslationIfMissing();
    }
  }

  submitForm() {
    this.setValuesOfTranslation();
    const newTranslations = <CategoryTranslationCollectionDto> {
      CategoryId: this.category.Id,
      Translations: this.categoryTranslations
    };
    this.activeModal.close(newTranslations);
  }

  validateChanges() {
    this.checkName();
    this.checkDescription();

    if (!this.nameMissing && !this.descriptionMissing) {
      this.submitForm();
    }
  }

  checkName() {
    this.nameMissing = !(!!this.myForm.get('EnglishName').value && this.myForm.get('EnglishName').value.trim() !== '');
  }

  checkDescription() {
    this.descriptionMissing = !(!!this.myForm.get('EnglishDescription').value && this.myForm.get('EnglishDescription').value.trim() !== '');
  }

  getSrcForLang(languageName: string): string {
    return this.flags.find(e => e.name === languageName).src;
  }

  adjustTextAreaHeight(event): void {
    const textArea = event.target;
    this.adjustElementHeight(textArea);
  }

  adjustElementHeight(textArea): void {
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 'px';
    textArea.style.overflow = 'hidden';
    textArea.parentElement.style.marginBottom = '20px';
  }

  private getValuesFromTranslationsToForm() {
    this.languages.forEach(lang => {
      const translation = this.categoryTranslations.find(t => t.LanguageId === lang.Id);
      if (!!translation) {
        this.myForm.controls[lang.Name + 'Name'].setValue(translation.Name);
        this.myForm.controls[lang.Name + 'Description'].setValue(translation.Description);
      }
    });
  }

  private setValuesOfTranslation() {
    this.languages.forEach(lang => {
      const nameControl = this.myForm.controls[lang.Name + 'Name'];
      const descriptionControl = this.myForm.controls[lang.Name + 'Description'];
      if (nameControl.dirty || descriptionControl.dirty) {
        this.editTranslation(nameControl, descriptionControl, lang);
      }
    });
  }

  private editTranslation(nameControl: AbstractControl, descriptionControl: AbstractControl, lang: Language) {
    const translation = this.categoryTranslations.find(t => t.LanguageId === lang.Id);
    const translatedName = nameControl.value;
    const translatedDescription = descriptionControl.value;

    if (!translation) {
      this.addNewTranslation(lang.Id, translatedName, translatedDescription);
    } else {
      translation.Name = translatedName;
      translation.Description = translatedDescription;
    }
  }

  private addNewTranslation(languageId: string, translatedName: string, translatedDescription: string) {
    const newTranslation = <CategoryTranslationDto>{};
    newTranslation.LanguageId = languageId;
    newTranslation.Name = translatedName;
    newTranslation.Description = translatedDescription;

    this.categoryTranslations.push(newTranslation);
  }

  private setEnglishTranslationIfMissing() {
    const englishLanguage = this.languages.find(l => l.Name === 'English');

    if (!!englishLanguage) {
      const englishTranslation = this.categoryTranslations.find(t => t.LanguageId === englishLanguage.Id);
      if (englishTranslation === undefined) {
        this.addNewTranslation(englishLanguage.Id, this.category.Name, this.category.Description);
      }
    }
  }
}
